import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/**
 * Challenge Summary Response
 * Complete data for post-completion Challenge Summary screen
 */
export interface ChallengeSummaryResponse {
  // Challenge info
  challenge: {
    id: string;
    name: string;
    type: string;
    dailyGoal: number;
    durationDays: number;
    requiredDays: number;
    stakeAmount: number;
    totalPrizePool: number;
    startDate: string;
    endDate: string;
    status: string;
  };

  // User's result
  userResult: {
    isWinner: boolean;
    payoutAmount: number;
    finalRank: number | null;
    totalParticipants: number;
  };

  // User's personal stats
  personalStats: {
    totalDistance: number; // km
    totalDuration: number; // seconds
    totalCalories: number;
    totalRuns: number;
    daysCompleted: number;
    longestRun: number; // km
    bestDay: { date: string; distance: number } | null;
    averagePace: string | null;
    longestStreak: number;
  };

  // Club (all participants) stats
  clubStats: {
    totalDistance: number; // km
    totalParticipants: number;
    totalWinners: number;
  };

  // Leaderboard context
  leaderboard: {
    closestRival: {
      username: string | null;
      avatarUrl: string | null;
      distanceAhead: number;
    } | null;
    topPerformers: Array<{
      rank: number;
      username: string | null;
      avatarUrl: string | null;
      walletAddress: string;
      totalDistance: number;
      daysCompleted: number;
      isWinner: boolean;
    }>;
  };
}

/**
 * Challenge API
 * Handles challenge-related API calls
 */
export const challengeApi = {
  /**
   * Get comprehensive challenge summary for a completed challenge
   * Only available for completed challenges where user is a participant
   *
   * @param challengeId - Challenge ID
   * @returns Complete challenge summary data
   */
  getSummary: async (challengeId: string): Promise<ChallengeSummaryResponse> => {
    const response = await apiClient.get(API_ENDPOINTS.CHALLENGES.SUMMARY(challengeId));
    return response.data.summary;
  },
};
